import axios from "axios";

const API = process.env.REACT_APP_BACK_END_API;
const cfg = { withCredentials: true } as const;

export type RiskLevel = "high" | "medium" | "low";

/** A SEARCH_RESULTS row joined with episode meta (from GET /search/results). */
export interface JudgeResultRow {
  ID: string;
  SEARCH_ID: string;
  TOPIC: string;
  REF_ID: string;
  TITLE: string;
  SCORE: number;
  RISK_LEVEL: RiskLevel;
  REASON: string;
  TIMECODES: string; // JSON string array
  SEASON?: number;
  EPISODE_NUMBER?: number;
  ORIGINAL_AIR_DATE?: string;
  RATING_US?: string;
  [k: string]: any;
}

export interface EpisodeListRow {
  REF_ID: string;
  TITLE: string;
  SEASON?: number;
  EPISODE_NUMBER?: number;
  CHUNK_COUNT: number; // total caption chunks
  CHUNKS_EMBEDDED: number; // how many of those chunks have an embedding
  CHUNK_SKIP_REASON?: string | null;
}


export const runSearch = (topic: string, forceRefresh = false) =>
  // Starts the run in the background and returns a searchId immediately, so this
  // request is quick. forceRefresh bypasses the topic cache and re-runs.
  axios.post(`${API}/api/search/run`, { topic, forceRefresh }, { ...cfg, timeout: 60 * 1000 }).then((r) => r.data);

export const getSearchStatus = (
  searchId: string
): Promise<{ status: "processing" | "done" | "error" | "unknown"; total?: number; error?: string }> =>
  axios.get(`${API}/api/search/status/${searchId}`, cfg).then((r) => r.data);

export const getResults = (searchId: string): Promise<{ searchId: string; results: JudgeResultRow[] }> =>
  axios.get(`${API}/api/search/results/${searchId}`, cfg).then((r) => r.data);

export const getEpisode = (refId: string) =>
  axios.get(`${API}/api/search/episode/${encodeURIComponent(refId)}`, cfg).then((r) => r.data);

export interface CandidateChunk {
  timecodeStart: string;
  chunkText: string;
  score: number;
  source: "keyword" | "semantic";
  fileName: string;
}

/** A Step-1 candidate (search engine output, before the LLM judge). */
export interface Candidate {
  refId: string;
  title: string;
  metaScore: number;
  chunkScore: number;
  finalScore: number;
  score: number;
  meta: Record<string, any>;
  chunks: CandidateChunk[];
  sources: {
    keywordMeta: number | null;
    semanticMeta: number | null;
    keywordChunk: number | null;
    semanticChunk: number | null;
  };
}

export interface CandidatesResponse {
  topic: string;
  status: "processing" | "done";
  cached?: boolean;
  candidateSearchId?: string;
  candidates?: Candidate[];
  total?: number;
}

export const getCandidates = (topic: string, forceRefresh = false): Promise<CandidatesResponse> =>
  // Step 1: starts the build (or returns the cache) and comes back quickly. A
  // fresh build runs in the background; poll getCandidateStatus until done.
  axios
    .post(`${API}/api/search/candidates`, { topic, forceRefresh }, { ...cfg, timeout: 60 * 1000 })
    .then((r) => r.data);

export const getCandidateStatus = (
  candidateSearchId: string
): Promise<{ status: "processing" | "done" | "error" | "unknown"; candidates?: Candidate[]; error?: string }> =>
  axios.get(`${API}/api/search/candidates/status/${candidateSearchId}`, cfg).then((r) => r.data);

export interface EpisodeListParams {
  page: number;
  pageSize: number;
  search?: string;
  status?: string; // all | ready | pending | skipped | not_processed
}

export const listEpisodes = (
  params: EpisodeListParams
): Promise<{ episodes: EpisodeListRow[]; total: number; page: number; pageSize: number }> =>
  axios.get(`${API}/api/episodes`, { ...cfg, params }).then((r) => r.data);

export interface EpisodeSummary {
  total: number;
  ready: number;
  pending: number;
  skipped: number;
  not_processed: number;
}

export const getEpisodeSummary = (): Promise<EpisodeSummary> =>
  axios.get(`${API}/api/episodes/summary`, cfg).then((r) => r.data);


export const riskColor = (risk?: string): string =>
  risk === "high" ? "red" : risk === "medium" ? "orange" : "green";

export const parseTimecodes = (raw?: string): string[] => {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.map(String) : [];
  } catch {
    return [];
  }
};
